import React, { useEffect, useRef } from 'react';

import Artifact from 'assets/images/about/artifact.png';
import Button from 'components/Button/Button';
import ChapterLabel from 'components/ChapterLabel/ChapterLabel';
import { useCopyStore, useGlobalStore } from 'store';
import { ReactComponent as ArrowCircleIcon } from 'svgs/arrowCircle.svg';
import { ReactComponent as CloseCircleIcon } from 'svgs/closeCircle.svg';
import gtm, { actions, categories } from 'u9/utils/gtm';

import * as Styled from './Sidebar.styles';

export interface SidebarProps {}

const defaultProps: Partial<SidebarProps> = {};

const Sidebar: React.FC<SidebarProps> = () => {
  const {
    copy: { about, accessibility },
  } = useCopyStore();
  const { isSidebarExpanded, setSidebarExpanded, isSmallDevice } =
    useGlobalStore();
  const closeBtnRef = useRef<any>(null);

  useEffect(() => {
    if (closeBtnRef?.current && isSidebarExpanded) {
      setTimeout(() => {
        closeBtnRef?.current?.focus();
      }, 600);
    }
  }, [closeBtnRef, isSidebarExpanded]);

  const handleClose = () => {
    setSidebarExpanded(false);
    gtm.trackEvent(categories.about, actions.click, about?.backBtn);
  };

  return (
    <Styled.Container
      isOpen={isSidebarExpanded}
      aria-hidden={!isSidebarExpanded}
    >
      <Styled.ButtonWrapper
        initial={{ opacity: 0, x: 50 }}
        animate={
          isSidebarExpanded ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }
        }
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {isSidebarExpanded && (
          <Button
            type="black"
            icon="arrowLeft"
            text={about?.backBtn}
            onClick={e => {
              e.preventDefault();
              handleClose();
            }}
            elementRef={isSmallDevice ? null : closeBtnRef}
          />
        )}
      </Styled.ButtonWrapper>
      <Styled.MobileButtonWrapper
        initial={{ opacity: 0 }}
        animate={isSidebarExpanded ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        onClick={handleClose}
        tabIndex={0}
        aria-label={accessibility?.shared?.aboutCloseBtn}
        ref={isSmallDevice ? closeBtnRef : null}
      >
        <CloseCircleIcon />
      </Styled.MobileButtonWrapper>
      <Styled.ContentWrapper>
        <Styled.ChapterLabelWrapper
          initial={{ opacity: 0, y: 50 }}
          animate={
            isSidebarExpanded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
          }
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <ChapterLabel title={about?.chapterLabel} as="h1" />
        </Styled.ChapterLabelWrapper>
        <Styled.ImageWrapper
          initial={{ opacity: 0 }}
          animate={isSidebarExpanded ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <img
            src={Artifact}
            alt={accessibility?.sidebar?.image || 'artifact'}
            aria-label={accessibility?.sidebar?.image}
          />
        </Styled.ImageWrapper>
        <Styled.Title
          initial={{ opacity: 0, y: 50 }}
          animate={
            isSidebarExpanded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
          }
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h2>{about?.title}</h2>
        </Styled.Title>
        <Styled.Description
          initial={{ opacity: 0, y: 50 }}
          animate={
            isSidebarExpanded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
          }
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p dangerouslySetInnerHTML={{ __html: about?.description }}></p>
        </Styled.Description>
        <Styled.Columns>
          {about?.columns.map(({ title, description, links }, index) => (
            <Styled.Column key={index}>
              <Styled.ColumnTitle
                initial={{ opacity: 0, y: 50 }}
                animate={
                  isSidebarExpanded
                    ? { opacity: 1, y: 0 }
                    : { opacity: 0, y: 50 }
                }
                transition={{ duration: 0.6, delay: 1 }}
              >
                <h3>{title}</h3>
              </Styled.ColumnTitle>
              <Styled.ColumnDescription
                initial={{ opacity: 0, y: 50 }}
                animate={
                  isSidebarExpanded
                    ? { opacity: 1, y: 0 }
                    : { opacity: 0, y: 50 }
                }
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <p dangerouslySetInnerHTML={{ __html: description }}></p>
              </Styled.ColumnDescription>
              {isSidebarExpanded && (
                <Styled.ColumnLinks>
                  {links?.map(({ name, link }, childIndex) => (
                    <Styled.Link
                      key={childIndex}
                      initial={{ opacity: 0, y: 50 }}
                      animate={
                        isSidebarExpanded
                          ? { opacity: 1, y: 0 }
                          : { opacity: 0, y: 50 }
                      }
                      transition={{ duration: 0.6, delay: 1.4 }}
                    >
                      <a
                        href={link}
                        onClick={() => {
                          gtm.trackEvent(categories.about, actions.click, name);
                        }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ArrowCircleIcon />
                        {name}
                      </a>
                    </Styled.Link>
                  ))}
                </Styled.ColumnLinks>
              )}
            </Styled.Column>
          ))}
        </Styled.Columns>
        {isSidebarExpanded && (
          <Styled.Footer>
            {about?.footerLinks?.map(({ name, link }, index) => (
              <Styled.FooterItem
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={
                  isSidebarExpanded
                    ? { opacity: 1, y: 0 }
                    : { opacity: 0, y: 50 }
                }
                transition={{ duration: 0.6, delay: 1.6 }}
              >
                <a
                  href={link}
                  onClick={() => {
                    gtm.trackEvent(categories.about, actions.click, name);
                  }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {name}
                </a>
              </Styled.FooterItem>
            ))}
            <Styled.FooterItem
              initial={{ opacity: 0, y: 50 }}
              animate={
                isSidebarExpanded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
              }
              transition={{ duration: 0.6, delay: 1.6 }}
            >
              <p dangerouslySetInnerHTML={{ __html: about?.copyrights }}></p>
            </Styled.FooterItem>
          </Styled.Footer>
        )}
      </Styled.ContentWrapper>
    </Styled.Container>
  );
};

Sidebar.defaultProps = defaultProps;

export default Sidebar;
